import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { FileText, Save, Loader2, Plus, X, ClipboardList } from "lucide-react";

function TemplateCard({ tpl, onSaved }) {
  const [intro, setIntro] = useState(tpl.intro || "");
  const [fields, setFields] = useState(tpl.fields || []);
  const [reqs, setReqs] = useState(tpl.requirements || []);
  const [saving, setSaving] = useState(false);
  const dirty = intro !== (tpl.intro || "") ||
    JSON.stringify(fields) !== JSON.stringify(tpl.fields || []) ||
    JSON.stringify(reqs) !== JSON.stringify(tpl.requirements || []);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put(`/search-templates/${encodeURIComponent(tpl.key)}`, {
        intro, fields: fields.filter((f) => f.trim()), requirements: reqs.filter((r) => r.trim()),
      });
      onSaved(data);
      toast.success(`Saved “${data.product}” instructions`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save"); }
    finally { setSaving(false); }
  };

  return (
    <div data-testid={`si-card-${tpl.key}`} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="flex items-center gap-2 text-lg font-700 text-[#1A395E]">
          <span className="inline-block h-4 w-1.5 rounded-full bg-teal" /> {tpl.product}
        </h2>
        <button data-testid={`si-save-${tpl.key}`} onClick={save} disabled={!dirty || saving}
          className="inline-flex items-center gap-1.5 rounded-lg bg-[#1A395E] px-3.5 py-1.5 text-[12px] font-semibold text-white hover:bg-[#24497A] transition-colors disabled:opacity-40">
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} {dirty ? "Save changes" : "Saved"}
        </button>
      </div>

      <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1">Intro line (acknowledgement request)</label>
      <textarea data-testid={`si-intro-${tpl.key}`} value={intro} onChange={(e) => setIntro(e.target.value)} rows={2}
        className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 focus:border-teal focus:outline-none focus:ring-1 focus:ring-teal" />

      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Header fields</label>
          <div className="space-y-1.5">
            {fields.map((f, i) => (
              <div key={i} className="flex items-center gap-2">
                <input data-testid={`si-field-${tpl.key}-${i}`} value={f}
                  onChange={(e) => setFields(fields.map((x, j) => j === i ? e.target.value : x))}
                  className="flex-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px] focus:border-teal focus:outline-none" />
                <button onClick={() => setFields(fields.filter((_, j) => j !== i))} className="rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><X className="h-4 w-4" /></button>
              </div>
            ))}
            <button data-testid={`si-add-field-${tpl.key}`} onClick={() => setFields([...fields, ""])}
              className="inline-flex items-center gap-1 text-[12px] font-semibold text-teal hover:underline"><Plus className="h-3.5 w-3.5" /> Add field</button>
          </div>
        </div>
        <div>
          <label className="block text-[11px] font-semibold uppercase tracking-wide text-slate-500 mb-1.5">Search requirements</label>
          <div className="space-y-1.5">
            {reqs.map((r, i) => (
              <div key={i} className="flex items-start gap-2">
                <textarea data-testid={`si-req-${tpl.key}-${i}`} value={r} rows={2}
                  onChange={(e) => setReqs(reqs.map((x, j) => j === i ? e.target.value : x))}
                  className="flex-1 rounded-md border border-slate-300 px-2.5 py-1.5 text-[13px] focus:border-teal focus:outline-none" />
                <button onClick={() => setReqs(reqs.filter((_, j) => j !== i))} className="mt-1 rounded-md p-1.5 text-slate-400 hover:bg-rose-50 hover:text-rose-600"><X className="h-4 w-4" /></button>
              </div>
            ))}
            <button data-testid={`si-add-req-${tpl.key}`} onClick={() => setReqs([...reqs, ""])}
              className="inline-flex items-center gap-1 text-[12px] font-semibold text-teal hover:underline"><Plus className="h-3.5 w-3.5" /> Add requirement</button>
          </div>
        </div>
      </div>
      {tpl.updated_by_name && <div className="mt-3 text-[11px] text-slate-400">Last edited by {tpl.updated_by_name}{tpl.updated_at ? ` · ${new Date(tpl.updated_at).toLocaleString()}` : ""}</div>}
    </div>
  );
}

export default function SearchInstructions() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/search-templates"); setTemplates(data || []); }
    catch { toast.error("Could not load templates"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onSaved = (updated) => setTemplates((ts) => ts.map((t) => t.key === updated.key ? updated : t));

  return (
    <div className="space-y-5">
      <div>
        <h1 className="font-display text-3xl font-700 tracking-tight text-slate-800 flex items-center gap-2">
          <ClipboardList className="h-7 w-7 text-teal" /> Search Instructions
        </h1>
        <p className="text-sm text-slate-500 mt-1 max-w-3xl">
          Edit the per-product search requirements sent to abstractors. These are automatically included in the
          hand-off email whenever an order is assigned to an abstractor (ProTitleUSA).
        </p>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Loading templates…</div>
      ) : (
        <div className="space-y-5">
          {templates.map((t) => <TemplateCard key={t.key} tpl={t} onSaved={onSaved} />)}
        </div>
      )}
    </div>
  );
}
